import { message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
    useAgentStatusChange, useAnswerSoftPhone, useGetSoftPhoneAuthStatus,
    useGetSoftPhoneToken, useHangUpSoftPhone, useLoginSoftPhone,
    useMuteSoftPhone, useProfile
    // Remove useAgentStatusManager import
} from 'hooks';
import { useOmniCallContext } from 'contexts';
import {
    useCreateAgentStatusChangeMutation, useLazyGetAgentStatusQuery,
    useLazyGetReasonsOptionsQuery
} from 'services';
import { AGENT_STATUS, CALLSTATUS, PERMISSION } from 'utils';
import { ChannelObject } from 'utils/constants/contact-event-channel.constant';
import FailedLoginContent from './FailedLoginCotent';
import IncomingCallContent from './IncomingCallContent';
import InCallContent from './InCallContent';
import CreatingTicketContent from './CreatingTicketContent';
import TemporarilyPausedContent from './TemporarilyPausedContent';
import ReadyStatusContent from './ReadyStatusContent';
import { useDispatch } from 'react-redux';
import FailedLoginWorldPhoneWarningModal from './FailedLoginWorldPhoneWarningModal';
import { useCreateContactEventMutation } from 'services/tickets';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { checkPermissions } from 'utils/check-permission';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import { socketService } from 'utils/agent-map/socket-service-singleton';
import PauseReasonModal from './PauseReasonModal';
import { useToggleScreenRecord } from 'hooks/screen-record';
import MissedCallModal from './MissedCallModal';

const sourceDisplayNames: Record<string, string> = {
    'telco': 'VNPT',
    'webcall': 'WEBCALL',
    'appcall': 'APPCALL',
    'audiocall': 'VOICE',
    'videocall': 'VIDEO',
};

// Map callSource to channelId
const sourceToChannelMapping: Record<string, number> = {
    'telco': ChannelObject.DIEN_THOAI.id,
    'webcall': ChannelObject.DIEN_THOAI_WEB.id,
    'appcall': ChannelObject.DIEN_THOAI_APP.id,
    'audiocall': ChannelObject.WEB360_AUDIO.id,
    'videocall': ChannelObject.WEB360_VIDEO.id,
};

const getSourceDisplayName = (source: string): string => {
    return sourceDisplayNames[source.toLowerCase()] || source.toUpperCase();
};

// Get channelId from callSource
const getChannelIdFromSource = (source: string): number => {
    return sourceToChannelMapping[source?.toLowerCase()]  // Default to DIEN_THOAI if mapping not found
};

// Add this debounce utility at the top of your component
const useDebounce = (fn: Function, ms = 500) => {
    const timer = useRef<any>();
    return (...args: any) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            fn(...args);
        }, ms);
    };
};

const CustomerLookupPhone = () => {
    // Initialize with UNKNOWN to prevent status change on mount
    const [currentState, setCurrentState] = useState<string>(AGENT_STATUS.UNKNOWN);

    // Add a flag to track first load
    const isFirstLoad = useRef(true);

    // ✅ ADD: Add ref to protect initial loading
    const initialLoadingProtectionRef = useRef(true);

    // Use the context directly instead of useAgentStatusManager
    const {
        // Destructure context values, ensure not to call context's updateAgentStatus functions
        // from the effect that is supposed to set local state.
        updateAgentStatusReady, // Example: if used, ensure correct context
        updateAgentStatusPause,
        updateAgentStatusCreatingTicket,
        updateAgentStatusFailedLogin,
        updateAgentStatusOnCall,
        updateAgentStatusMuteCall,
        updateAgentStatusRinging, // We will react to agentStatus, not call this from the sync effect
        toggleAgentStatus,
        toggleAgentStatusWithoutChangingLayout,
        isTogglingStatus,
        isRemovingFromQueue,
        isStatusTransitioning,
        socketData,
        agentStatus, // This is the source of truth from context
        setGetAgentStatus,
        // Add missed call state
        showMissedCallModal,
        missedCallData,
        handleMissedCallModalClose
    } = useOmniCallContext();

    const { onLoginSoftPhoneHandle } = useLoginSoftPhone();
    const { isAuthenticatedSoftPhone } = useGetSoftPhoneAuthStatus();
    const { onAnswerSoftPhoneHandle } = useAnswerSoftPhone();
    const { onHangUpSoftPhoneHandle } = useHangUpSoftPhone();
    const { onMuteSoftPhoneHandle } = useMuteSoftPhone();
    const { isAuthenticated, profile } = useProfile();
    const { onGetWorldPhoneToken } = useGetSoftPhoneToken();
    const { softPhoneToken } = useGetSoftPhoneAuthStatus();
    const [fetchAgentStatus, { data: currentFetchAgentStatus }] = useLazyGetAgentStatusQuery();
    const [seconds, setSeconds] = useState(0);
    // Add interval reference to manage the timer
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // Add call start time reference to calculate proper duration
    const callStartTimeRef = useRef<number>(0);
    const { onCreateAgentStatusChangeHandle } = useAgentStatusChange()
    const dispatch = useDispatch()
    const isAgentCreatingTicket = localStorage.getItem("isAgentCreatingTicket")
    const [onCreateContactEvent] = useCreateContactEventMutation();
    const [isAnswering, setIsAnswering] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false); // Add state for login loading
    const [isMuting, setIsMuting] = useState(false); // Add state for mute loading
    const [failedLoginWorldPhoneWarning, setFailedLoginWorldPhoneWarning] = useState('');
    const [openFailedLoginWorldPhoneWarning, setOpenFailedLoginWorldPhoneWarning] = useState(false);
    const { userPermissionIds } = useProfile();
    const isAllowChatCustomer = checkPermissions(userPermissionIds, PERMISSION.CHAT_CUSTOMER);
    const { onToggleScreenRecordHandle } = useToggleScreenRecord()
    const lastScreenRecordTime = useRef<number>(0); // Add new ref to track screen recording calls

    // Add these refs to track initialization and transitions
    const hasInitializedStatus = useRef(false);
    const previousStatus = useRef<string | undefined>(undefined);
    const lastQueueOpTime = useRef<number>(0); // Ref to track last queue operation time

    // ✅ ADD: Thêm refs để theo dõi và bảo vệ trạng thái RINGING
    // const lastRingingTimeRef = useRef<number>(0); // REMOVE
    // const forceRingingDisplayRef = useRef<boolean>(false); // REMOVE

    const [showPauseReasonModal, setShowPauseReasonModal] = useState(false);
    const [selectedPauseReason, setSelectedPauseReason] = useState<number>();
    const [fetchReasons, { data: reasonsData, isLoading: isLoadingReasons }] = useLazyGetReasonsOptionsQuery();
    const now = Date.now();
    const timeSinceLastRecord = now - lastScreenRecordTime.current;
    // Use hook's updateAgentStatusCreatingTicketWithoutCreateAgentStatusChange instead of local version
    const updateAgentStatusCreatingTicketWithoutCreateAgentStatusChange = () => {

        toggleAgentStatusWithoutChangingLayout(AGENT_STATUS.AGENT_PAUSED)
            .then(() => {
                setCurrentState(AGENT_STATUS.CREATING_TICKET);
                setGetAgentStatus({
                    worldPhoneState: AGENT_STATUS.IDLE,
                    agentState: AGENT_STATUS.CREATING_TICKET
                });
                localStorage.setItem("isAgentCreatingTicket", "1");
            })
            .catch(error => {
                console.error("Failed to pause agent before setting CREATING_TICKET state:", error);
                message.error("Không thể chuyển sang trạng thái tạo phiếu");
            });
    }

    // Create a debounced version of fetchAgentStatus
    const debouncedFetchStatus = useDebounce(() => {
        if (profile?.extensionId) {

            fetchAgentStatus({ extension: profile.extensionId });
        }
    }, 1000);

    // Replace ALL useEffects that fetch agent status with this single one
    useEffect(() => {


        // Only fetch on first mount
        if (profile?.extensionId) {
            fetchAgentStatus({ extension: profile.extensionId });
        }

        // Set up a gentle polling interval
        // const intervalId = setInterval(() => {
        //
        //     if (profile?.extensionId) {
        //         fetchAgentStatus({ extension: profile.extensionId });
        //     }
        // }, 15000); // Poll every 15 seconds

        // return () => {
        //     clearInterval(intervalId);
        // };
    }, [profile?.extensionId]); // Only depend on extension ID, which shouldn't change

    const handleFetchStatus = () => {
        if (profile?.extensionId) {
            fetchAgentStatus({ extension: profile.extensionId });
        }
    };

    // Add a new function to specifically record LOGIN status
    const recordLoginStatus = async () => {
        if (!profile?.userId || !profile?.extensionId) return;


        try {
            await onCreateAgentStatusChangeHandle({
                userId: profile.userId,
                extensionId: profile.extensionId,
                previousState: AGENT_STATUS.UNKNOWN,
                nextState: AGENT_STATUS.LOGIN,
            });

        } catch (error) {
            console.error('❌ Failed to record LOGIN status:', error);
        }
    };

    // Add a new useEffect to record LOGIN status when component mounts
    useEffect(() => {
        // Only run on initial mount if already authenticated
        if (isAuthenticated && profile?.userId && profile?.extensionId) {

            //recordLoginStatus();
        }
    }, [isAuthenticated, profile?.userId, profile?.extensionId]);

    // ✅ ADD: Track login attempt and protect status
    const [isLoginInProgress, setIsLoginInProgress] = useState(false);
    const loginAttemptTimeRef = useRef<number>(0);
    const lastSuccessfulStatusRef = useRef<string | null>(null);

    // ✅ MODIFY: Enhanced fetchAndLoginSoftPhone with better status protection
    const fetchAndLoginSoftPhone = async () => {
        if (!isAuthenticated) return;
        if (!profile?.extensionId) {
            setFailedLoginWorldPhoneWarning(customerLookupMessages.noExtensionFound);
            setOpenFailedLoginWorldPhoneWarning(true);
            return;
        }

        setIsLoggingIn(true);
        setIsLoginInProgress(true); // ✅ NEW: Track login progress
        loginAttemptTimeRef.current = Date.now(); // ✅ NEW: Record login time

        try {
            // First record LOGIN status
            await recordLoginStatus();

            // Add a small delay to ensure LOGIN is recorded before proceeding
            await new Promise(resolve => setTimeout(resolve, 300));

            const res = await onGetWorldPhoneToken({ userExtension: profile.extensionId });

            if (res?.data?.token) {
                try {

                    const response = await onLoginSoftPhoneHandle({
                        username: profile.username,
                        token: res.data.token,
                    });

                    if (response?.status === 1) {

                        // ✅ NEW: Save successful status to prevent regression
                        lastSuccessfulStatusRef.current = AGENT_STATUS.AGENT_PAUSED;

                        // Set agent to PAUSED after successful login
                        updateAgentStatusPause();

                        // ✅ NEW: Force fetch status after successful login with delay
                        setTimeout(() => {
                            if (profile?.extensionId) {
                                fetchAgentStatus({ extension: profile.extensionId });
                            }
                        }, 2000); // 2 second delay for API to update

                        // ✅ NEW: Delay clearing login flag to protect status
                        setTimeout(() => {
                            setIsLoginInProgress(false);
                        }, 5000); // 5 second protection window
                    } else {
                        console.error('❌ WorldPhone login failed with status:', response?.status);
                        setIsLoginInProgress(false);
                    }
                } catch (error) {
                    console.error('❌ WorldPhone login error:', error);
                    setOpenFailedLoginWorldPhoneWarning(true);
                    setFailedLoginWorldPhoneWarning(customerLookupMessages.openWorldPhoneWarning);
                    setIsLoginInProgress(false);
                }
            }
        } catch (error) {
            console.error('❌ Token fetch error:', error);
            setIsLoginInProgress(false);
        } finally {
            setIsLoggingIn(false);
        }
    };


    // Add function to start the timer
    const startTimer = () => {
        // Clear any existing timer first
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        // Set the call start time to current timestamp
        callStartTimeRef.current = Date.now();

        // Reset seconds to 0
        setSeconds(0);

        // Create a new interval that updates every second
        timerIntervalRef.current = setInterval(() => {
            // Calculate elapsed seconds since call start
            const elapsedSeconds = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
            setSeconds(elapsedSeconds);
        }, 1000);

    };

    // Add function to stop the timer
    const stopTimer = () => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
        setSeconds(0);
    };

    // Make sure to clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (currentState === AGENT_STATUS.ONCALL) {
            startTimer();
        } else if (currentState !== AGENT_STATUS.ONCALL && timerIntervalRef.current) {
            stopTimer();
        }

        // Cleanup khi component unmount
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, [currentState]);

    const answerSoftPhone = async () => {
        const userExtension = profile?.extensionId;

        if (!isAuthenticatedSoftPhone || !userExtension) return;

        try {
            // Set loading state to true
            setIsAnswering(true);

            const res = await onGetWorldPhoneToken({ userExtension });
            if (res?.data?.token) {
                const response = await onAnswerSoftPhoneHandle({ token: res.data.token });
                const { status } = response
                switch (status) {
                    case 1:
                        updateAgentStatusOnCall();

                        // Start the timer when call is successfully answered
                        startTimer();

                        if (timeSinceLastRecord > 5000) {

                            lastScreenRecordTime.current = now;

                            onToggleScreenRecordHandle({
                                isRec: true,
                                fileName: `${socketData?.parentCallUuid || Date.now()}.mp4`, // Use callUuid for unique filename
                                id: socketData?.parentCallUuid || "call"
                            }).then(response => {

                            }).catch(error => {
                                console.error('❌ Failed to start screen recording:', error);
                            });
                        } else {

                        }
                        message.success('Trả lời cuộc gọi thành công');
                        break;
                    case 41:
                        message.error('Token không hợp lệ');
                        updateAgentStatusFailedLogin()
                        break;
                    case 42:
                        message.error('Không có tài khoản SIP');
                        updateAgentStatusFailedLogin()
                        break;
                    case 43:
                        message.error('Tài khoản SIP không online');
                        updateAgentStatusFailedLogin()
                        break;
                    case 44:
                        message.error('Người dùng hiện đang không trong cuộc gọi');
                        break;
                    default:
                        break;
                }
            }
        } catch (error) {
            message.error("Gặp lỗi khi trả lời cuộc gọi");
            throw error;
        } finally {
            setIsAnswering(false);
        }
    };


    const hangUpSoftPhone = async () => {
        try {
            const userExtension = profile?.extensionId;
            if (!isAuthenticatedSoftPhone || !userExtension) return;

            await onGetWorldPhoneToken({ userExtension });

            if (softPhoneToken) {
                const response = await onHangUpSoftPhoneHandle({ token: softPhoneToken });
                const { status } = response
                switch (status) {
                    case 1:
                        // Stop the timer when call ends
                        stopTimer();

                        updateAgentStatusCreatingTicket()
                        // Clear mute state when call ends
                        saveMuteStateToLocalStorage(false);
                        setIsMuted(false);
                        message.success('Gác máy thành công');
                        break;
                    case 51:
                        message.error('Token không hợp lệ');
                        updateAgentStatusFailedLogin()
                        break;
                    case 52:
                        message.error('Không có tài khoản SIP');
                        updateAgentStatusFailedLogin()
                        break;
                    case 53:
                        message.error('Tài khoản SIP không online');
                        updateAgentStatusFailedLogin()
                        break;
                    case 54:
                        message.error('Người dùng hiện đang không trong cuộc gọi');
                        setCurrentState(AGENT_STATUS.AGENT_UNPAUSED);
                        break;
                    default:
                        break;
                }
            }
        } catch (error) {
            console.error("Gặp lỗi khi gác máy", error);
        }
    };


    // Add this function to manage mute state in localStorage
    const saveMuteStateToLocalStorage = (isMuted: boolean) => {
        try {
            // Get current call info
            const callData = getStoredCallData();
            if (callData) {
                const callId = callData.parentCallUuid || callData.callUuid;
                if (isMuted) {
                    localStorage.setItem(`mute_state_${callId}`, 'true');

                } else {
                    localStorage.removeItem(`mute_state_${callId}`);

                }
            }
        } catch (error) {
            console.error('Error saving mute state to localStorage:', error);
        }
    };

    // Check if call is muted based on localStorage
    const isCallMuted = () => {
        try {
            const callData = getStoredCallData();
            if (callData) {
                const callId = callData.parentCallUuid || callData.callUuid;
                return localStorage.getItem(`mute_state_${callId}`) === 'true';
            }
        } catch (error) {
            console.error('Error checking mute state in localStorage:', error);
        }
        return false;
    };

    // Add state to track mute status
    const [isMuted, setIsMuted] = useState(false);

    // Check for stored mute state when in a call
    useEffect(() => {
        if (currentState === AGENT_STATUS.ONCALL || currentState === AGENT_STATUS.MUTE_ON) {
            const storedMuteState = isCallMuted();
            if (storedMuteState) {

                setIsMuted(true);
                if (currentState !== AGENT_STATUS.MUTE_ON) {
                    updateAgentStatusMuteCall();
                }
            } else {
                setIsMuted(false);
            }
        }
    }, [currentState]);

    // Modify the muteSoftPhone function to save state to localStorage
    const muteSoftPhone = async (isMuteAction: boolean) => {
        setIsMuting(true);
        try {
            const userExtension = profile?.extensionId;
            if (!isAuthenticatedSoftPhone || !userExtension) return;

            const action = isMuteAction ? 'yes' : 'no'
            if (softPhoneToken) {
                const response = await onMuteSoftPhoneHandle({ token: softPhoneToken, mute: action, extension: userExtension });
                const { status } = response
                if (status === 1) {
                    if (isMuteAction) {
                        message.success('Tắt tiếng điện thoại thành công');
                        updateAgentStatusMuteCall();
                        setIsMuted(true);
                        saveMuteStateToLocalStorage(true);
                    }
                    else {
                        message.success('Bật tiếng điện thoại thành công');
                        updateAgentStatusOnCall();
                        setIsMuted(false);
                        saveMuteStateToLocalStorage(false);
                    }
                }
                else {
                    message.error('Thao tác không thành công');
                }
            }
        } catch (error) {
            console.error("Gặp lỗi khi thao tác mute/unmute", error);
        } finally {
            setIsMuting(false);
        }
    };

    // Add this function to retrieve call data from localStorage
    const getStoredCallData = () => {
        try {
            const storedData = localStorage.getItem('currentCallDialingEvent');
            if (storedData) {
                return JSON.parse(storedData);
            }
            return null;
        } catch (error) {
            console.error('Error retrieving stored call data:', error);
            return null;
        }
    };

    // Add state for restored call data
    const [restoredCallData, setRestoredCallData] = useState<any>(null);

    // Add useEffect to check for stored data on component mount when in ONCALL state
    useEffect(() => {
        // Check if we're in ONCALL state but have no socketData (after page refresh)
        if ((currentState === AGENT_STATUS.ONCALL || currentState === AGENT_STATUS.MUTE_ON || currentState === AGENT_STATUS.RINGING) && !socketData) {
            const storedCallData = getStoredCallData();
            if (storedCallData) {

                setRestoredCallData(storedCallData);
            }
        }
    }, [currentState, socketData]);

    // Modified effect for status changes to correctly update local currentState
    useEffect(() => {
        // If context status is undefined and it's not the initial load, treat as FAILED_LOGIN
        if (!agentStatus && !isFirstLoad.current && !isLoginInProgress) {
            setCurrentState(AGENT_STATUS.FAILED_LOGIN);
            previousStatus.current = AGENT_STATUS.FAILED_LOGIN;
            return;
        }

        // Handle first load separately
        if (isFirstLoad.current) {
            if (agentStatus && agentStatus !== AGENT_STATUS.UNKNOWN && agentStatus !== AGENT_STATUS.LOGGEDOFF) {
                setCurrentState(agentStatus);
                previousStatus.current = agentStatus;
                if (agentStatus === AGENT_STATUS.AGENT_PAUSED && profile?.userId && profile.extensionId) {
                    onCreateAgentStatusChangeHandle({
                        userId: profile.userId,
                        extensionId: profile.extensionId.toString(),
                        previousState: 'LOGIN',
                        nextState: AGENT_STATUS.AGENT_PAUSED
                    });
                }
            } else {
                setCurrentState(AGENT_STATUS.FAILED_LOGIN);
                previousStatus.current = AGENT_STATUS.FAILED_LOGIN;
            }
            isFirstLoad.current = false;
            return;
        }

        // If local state already matches context state, do nothing further
        if (currentState === agentStatus) {
            previousStatus.current = currentState; // Keep previousStatus updated
            return;
        }

        // Login protection window
        const timeSinceLogin = Date.now() - loginAttemptTimeRef.current;
        const isWithinLoginWindow = timeSinceLogin < 10000; // 10 second protection window
        if (isLoginInProgress || isWithinLoginWindow) {
            if ((agentStatus === AGENT_STATUS.UNKNOWN || agentStatus === AGENT_STATUS.LOGGEDOFF) && lastSuccessfulStatusRef.current) {
                previousStatus.current = currentState; // Don't change local state
                return;
            }
            if (agentStatus && agentStatus !== AGENT_STATUS.UNKNOWN && agentStatus !== AGENT_STATUS.LOGGEDOFF) {
                lastSuccessfulStatusRef.current = agentStatus; // Track successful status during/after login
            }
        }

        // Update local currentState based on context's agentStatus
        if (agentStatus) {

            // Directly set local state based on context, special handling for IDLE or localStorage overrides
            const localIsAgentCreatingTicket = localStorage.getItem("isAgentCreatingTicket");

            if (localIsAgentCreatingTicket === "1" && (agentStatus === AGENT_STATUS.AGENT_PAUSED || agentStatus === AGENT_STATUS.IDLE || agentStatus === AGENT_STATUS.AGENT_UNPAUSED)) {
                // If localStorage says CREATING_TICKET, it takes precedence over PAUSED/IDLE/UNPAUSED from context
                setCurrentState(AGENT_STATUS.CREATING_TICKET);
            } else if (agentStatus === AGENT_STATUS.IDLE) {
                // Resolve IDLE based on queue status if not overridden by CREATING_TICKET
                if (!currentFetchAgentStatus?.data?.[0]?.queues?.queue?.length) {
                    setCurrentState(AGENT_STATUS.AGENT_PAUSED);
                } else {
                    setCurrentState(AGENT_STATUS.AGENT_UNPAUSED);
                }
            } else {
                // Default: local state mirrors context state
                setCurrentState(agentStatus);
            }
        } else if (!isLoginInProgress) { // If agentStatus is null/undefined, and not in login process
            setCurrentState(AGENT_STATUS.FAILED_LOGIN);
        }

        previousStatus.current = currentState; // Update after potential setCurrentState

    }, [agentStatus, profile, onCreateAgentStatusChangeHandle, currentFetchAgentStatus, isLoginInProgress]); // Ensure all dependencies are correct

    const renderStateContent = () => {
        // console.log('🖥️ CLP Rendering UI for local state:', currentState, 'Context agentStatus:', agentStatus); // Debug line

        const commonProps = {
            isMinimized: isAllowChatCustomer ? true : false,
            isTogglingStatus: isTogglingStatus
        };

        // Use local `currentState` for rendering decisions, as it's synced from context `agentStatus`
        // with appropriate logic.

        // Initial loading protection for rendering
        // Use agentStatus from context for this check as local currentState might not be FAILED_LOGIN yet
        if (initialLoadingProtectionRef.current && (agentStatus === AGENT_STATUS.UNKNOWN || agentStatus === AGENT_STATUS.LOGGEDOFF || !agentStatus)) {
            return <FailedLoginContent {...commonProps}
                fetchAndLoginSoftPhone={fetchAndLoginSoftPhone}
                isLoggingIn={isLoggingIn} />;
        }

        // Login in progress protection for rendering
        if (isLoginInProgress && lastSuccessfulStatusRef.current && lastSuccessfulStatusRef.current !== AGENT_STATUS.FAILED_LOGIN) {
            // Render based on lastSuccessfulStatusRef.current
            // This logic needs to be robust. For now, let the main switch use `currentState`.
        }

        const effectiveSocketData =
            (currentState === AGENT_STATUS.ONCALL || currentState === AGENT_STATUS.MUTE_ON || currentState === AGENT_STATUS.RINGING) &&
                !socketData && restoredCallData
                ? restoredCallData
                : socketData;

        switch (currentState) { // Switch on local currentState
            case AGENT_STATUS.UNKNOWN:
            case AGENT_STATUS.LOGGEDOFF:
            case AGENT_STATUS.FAILED_LOGIN:
                return <FailedLoginContent {...commonProps} fetchAndLoginSoftPhone={fetchAndLoginSoftPhone} isLoggingIn={isLoggingIn} />;
            case AGENT_STATUS.RINGING:
                return <IncomingCallContent
                    {...commonProps}
                    answerSoftPhone={answerSoftPhone}
                    socketData={effectiveSocketData}
                    getSourceDisplayName={getSourceDisplayName}
                    isAnswering={isAnswering}
                />;
            case AGENT_STATUS.ONCALL:
                return <InCallContent
                    {...commonProps}
                    hangUpSoftPhone={hangUpSoftPhone}
                    muteSoftPhone={muteSoftPhone}
                    currentState={currentState} // Pass local currentState
                    socketData={effectiveSocketData}
                    getSourceDisplayName={getSourceDisplayName}
                    isMuted={isMuted}
                    isMuting={isMuting}
                    seconds={seconds}
                />;
            case AGENT_STATUS.MUTE_ON:
                return <InCallContent
                    {...commonProps}
                    hangUpSoftPhone={hangUpSoftPhone}
                    muteSoftPhone={muteSoftPhone}
                    currentState={currentState} // Pass local currentState
                    socketData={effectiveSocketData}
                    getSourceDisplayName={getSourceDisplayName}
                    isMuted={true}
                    isMuting={isMuting}
                    seconds={seconds}
                />;
            case AGENT_STATUS.CREATING_TICKET:
                return <CreatingTicketContent
                    {...commonProps}
                    toggleAgentStatus={toggleAgentStatus}
                    showPauseReasonModal={showPauseReasonModal}
                    setShowPauseReasonModal={setShowPauseReasonModal}
                    customerPhoneNumber={effectiveSocketData?.customerNumber}
                />;
            case AGENT_STATUS.AGENT_PAUSED:
                return <TemporarilyPausedContent {...commonProps} toggleAgentStatus={toggleAgentStatus} />;
            case AGENT_STATUS.AGENT_UNPAUSED:
                return <ReadyStatusContent {...commonProps} toggleAgentStatus={toggleAgentStatus} showPauseReasonModal={showPauseReasonModal} setShowPauseReasonModal={setShowPauseReasonModal} />;
            // IDLE case should have been resolved to PAUSED or UNPAUSED by the useEffect
            // If currentState can still be IDLE here, it needs specific rendering.
            // For safety, add a default or handle IDLE explicitly if it's a possible local state.
            default:
                return <FailedLoginContent {...commonProps} fetchAndLoginSoftPhone={fetchAndLoginSoftPhone} isLoggingIn={isLoggingIn} />;
        }
    };

    // re-establish socket connection when window/tab regains focus
    // useEffect(() => {
    //     const handleFocus = () => {

    //         socketService.reinitialize();
    //     };
    //     window.addEventListener('focus', handleFocus);
    //     return () => {
    //         window.removeEventListener('focus', handleFocus);
    //     };
    // }, []);

    return (
        <div className='h-full'>
            <FailedLoginWorldPhoneWarningModal
                open={openFailedLoginWorldPhoneWarning}
                onCancel={() => {
                    setOpenFailedLoginWorldPhoneWarning(false);
                }}
                content={failedLoginWorldPhoneWarning}
            />

            <MissedCallModal
                open={showMissedCallModal}
                onClose={handleMissedCallModalClose}
                customerNumber={missedCallData.customerNumber}
                callSource={missedCallData.callSource}
                callTime={missedCallData.callTime}
            />

            {renderStateContent()}
            <PauseReasonModal
                isTogglingStatus={isTogglingStatus}
                toggleAgentStatus={toggleAgentStatus}
                showPauseReasonModal={showPauseReasonModal}
                setShowPauseReasonModal={setShowPauseReasonModal}
                setSelectedPauseReason={setSelectedPauseReason}
                selectedPauseReason={selectedPauseReason}
            />
        </div>
    );
};

export default CustomerLookupPhone;
